import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import { getWeather } from "../lib/api";
import { temp, tempUnit } from "../lib/weather";

const LS_KEY = "aethera_daily_summary";
const DEFAULTS = { enabled: false, time: "07:00", lastSent: null };

const locKey = (l) => `${l.lat.toFixed(2)},${l.lon.toFixed(2)}`;

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const loadSettings = () => {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(LS_KEY) || "{}") };
  } catch {
    return DEFAULTS;
  }
};

export const summaryLine = (c, units) =>
  `${c.loc.name}: ${c.day.condition.description}, ${temp(c.day.temp_min, units)}–${temp(c.day.temp_max, units)}${tempUnit(units)}, hujan ${c.day.pop}%`;

export const useDailySummary = ({ home, favorites, current, units }) => {
  const [settings, setSettings] = useState(loadSettings);
  const [open, setOpen] = useState(false);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const busy = useRef(false);

  const cities = useMemo(() => {
    const seen = new Set();
    const list = [home, ...favorites].filter(Boolean).filter((l) => {
      const k = locKey(l);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    return list.length ? list : current ? [current] : [];
  }, [home, favorites, current]);
  const citiesKey = cities.map(locKey).join("|");

  const update = useCallback((patch) => {
    setSettings((s) => {
      const next = { ...s, ...patch };
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const build = useCallback(async () => {
    setLoading(true);
    const res = await Promise.all(
      cities.map((l) =>
        getWeather(l)
          .then((d) => ({ loc: l, day: d.daily[0], current: d.current, isHome: home && locKey(home) === locKey(l) }))
          .catch(() => null)
      )
    );
    const list = res.filter(Boolean);
    setCards(list);
    setLoading(false);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citiesKey, home]);

  const showNow = useCallback(() => {
    setOpen(true);
    build();
  }, [build]);

  const deliver = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const list = await build();
      if (!list.length) return;
      update({ lastSent: todayStr() });
      const body = list.map((c) => summaryLine(c, units)).join("\n");
      setOpen(true);
      toast.info("Ringkasan cuaca pagi ini — KayongWeather", { description: body, duration: 12000 });
      if ("Notification" in window && Notification.permission === "granted") {
        try {
          new Notification("Ringkasan Pagi — KayongWeather", { body });
        } catch {
          /* ignore */
        }
      }
    } finally {
      busy.current = false;
    }
  }, [build, update, units]);

  useEffect(() => {
    if (!settings.enabled || !cities.length) return;
    const check = () => {
      const now = new Date();
      const [h, m] = settings.time.split(":").map(Number);
      const due = now.getHours() * 60 + now.getMinutes() >= h * 60 + m;
      if (due && settings.lastSent !== todayStr()) deliver();
    };
    check();
    const t = setInterval(check, 60 * 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.enabled, settings.time, settings.lastSent, citiesKey]);

  const toggleEnabled = useCallback(async () => {
    const next = !settings.enabled;
    if (next && "Notification" in window && Notification.permission === "default") {
      try {
        await Notification.requestPermission();
      } catch {
        /* ignore */
      }
    }
    update({ enabled: next, lastSent: next ? settings.lastSent : null });
    if (next) toast.success(`Ringkasan pagi aktif setiap ${settings.time}`);
    else toast("Ringkasan pagi dimatikan");
  }, [settings.enabled, settings.time, settings.lastSent, update]);

  return {
    settings,
    setTime: (time) => update({ time, lastSent: null }),
    toggleEnabled,
    open,
    setOpen,
    cards,
    loading,
    showNow,
    cityCount: cities.length,
  };
};
